import React, { useState, useEffect } from 'react';
import { 
  CssBaseline, 
  Box, 
  Typography, 
  Input, 
  Button, 
  FormControl, 
  FormLabel,
  Alert,
  CircularProgress,
  Stack,
  Sheet
} from '@mui/joy';
import { useNavigate, useLocation } from 'react-router-dom';

function AdminLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  // Use preloaded app name if available
  const [appName, setAppName] = useState(window.appSettings?.appName || 'QuEry-Tool');
  const logoUrl = `/logo.svg?t=${Date.now()}`; // Add cache busting

  // Get app settings if not already preloaded
  useEffect(() => {
    if (!window.appSettings) {
      async function loadAppearanceSettings() {
        try {
          const response = await fetch('/api/appearance/settings');
          if (response.ok) {
            const settings = await response.json();
            if (settings.appName) {
              setAppName(settings.appName);
              document.title = `${settings.appName} Admin Login`;
              window.appSettings = settings;
            } else {
              document.title = 'QuEry-Tool Admin Login';
            }
          } else {
            document.title = 'QuEry-Tool Admin Login';
          }
        } catch (error) {
          console.error('Error loading appearance settings:', error);
          document.title = 'QuEry-Tool Admin Login';
        }
      }
      loadAppearanceSettings();
    } else if (window.appSettings.appName) {
      document.title = `${window.appSettings.appName} Admin Login`;
    }
  }, [appName]);

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const response = await fetch('/api/admin/auth-status');
        const data = await response.json();
        
        if (data.isAuthenticated) {
          // Get the redirect path from location state or default to /admin
          const from = location.state?.from?.pathname || '/admin';
          navigate(from, { replace: true });
        }
      } catch (error) {
        console.error('Error checking authentication status:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkAuthStatus();
  }, [navigate, location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    if (!username || !password) {
      setError('Bitte Benutzername und Passwort eingeben');
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password }),
        credentials: 'include' // Important for cookies
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Login fehlgeschlagen');
      }
      
      setPassword(''); // Clear password for security
      
      // Get the redirect path from location state or default to /admin
      const from = location.state?.from?.pathname || '/admin';
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Login fehlgeschlagen, bitte versuchen Sie es erneut');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Show loading spinner while checking authentication
  if (isCheckingAuth) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <CssBaseline>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <Sheet
          sx={{
            width: 400,
            mx: 'auto',
            p: 3,
            borderRadius: 'md'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <img src={logoUrl} alt="Logo" style={{ height: 70, marginBottom: 16 }} />
            <Typography level="h3">{appName} Admin Login</Typography>
          </Box>
          
          {error && (
            <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Benutzername</FormLabel>
                <Input
                  fullWidth
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus
                  disabled={isLoading}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Passwort</FormLabel>
                <Input
                  fullWidth
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </FormControl>
              
              <Button
                type="submit"
                fullWidth
                loading={isLoading}
                sx={{ mt: 2 }}
              >
                {isLoading ? 'Anmeldung...' : 'Anmelden'}
              </Button>
              
              <Button
                variant="outlined"
                color="neutral"
                fullWidth
                onClick={() => navigate('/')}
                disabled={isLoading}
              >
                Zurück zur Startseite
              </Button>
            </Stack>
          </form>
        </Sheet>
      </Box>
    </CssBaseline>
  );
}

export default AdminLogin;
